import React from "react"
import { graphql, useFragment } from "react-relay"
import type { Transaction_metadata$key } from "@/lib/graphql/__generated__/Transaction_metadata.graphql"
import type { Transaction_receipt$key } from "@/lib/graphql/__generated__/Transaction_receipt.graphql"
import type { Transaction_userTransaction$key } from "@/lib/graphql/__generated__/Transaction_userTransaction.graphql"
import { usePrivyWallet } from "@/providers/Privy/usePrivyWallet"
import { useConnectedWallet } from "@/providers/Wallet/selectors"
import { WALLET_NAME } from "@/constants/wallet"
import type { TxState } from "./types"
import { AcceptOffer } from "./variants/AcceptOffer.react"
import { ApproveCollection } from "./variants/ApproveCollection.react"
import { ApproveCurrency } from "./variants/ApproveCurrency.react"
import { BulkTransfer } from "./variants/BulkTransfer.react"
import { CancelAllOrders } from "./variants/CancelAllOrders.react"
import { CancelDeal } from "./variants/CancelDeal.react"
import { CancelOrder } from "./variants/CancelOrder.react"
import { ConfigureDrop } from "./variants/ConfigureDrop.react"
import { CreateDropTransaction } from "./variants/CreateDropTransaction.react"
import { DealAccept } from "./variants/DealAccept.react"
import { Mint } from "./variants/Mint.react"
import { OfferAccepted } from "./variants/OfferAccepted.react"
import { OpenSeaProTrade } from "./variants/OpenSeaProTrade.react"
import { PaymentAssetTransfer } from "./variants/PaymentAssetTransfer.react"
import { Purchase } from "./variants/Purchase.react"
import { Redeem } from "./variants/Redeem.react"
import { Sale } from "./variants/Sale.react"
import { Transfer } from "./variants/Transfer.react"
import { UnknownTransaction } from "./variants/UnknownTransaction.react"
import { Unwrap } from "./variants/Unwrap.react"
import { Wrap } from "./variants/Wrap.react"
import { Swap } from "./variants/Swap"
import { Receive } from "./variants/Receive"

type TransactionProps = {
  userTransaction: Transaction_userTransaction$key | null | undefined
  receipt: Transaction_receipt$key | null | undefined
  metadata: Transaction_metadata$key | null | undefined
  isCancelled?: boolean
}

export function Transaction({
  userTransaction: userTransactionDataKey,
  receipt: receiptDataKey,
  metadata: metadataDataKey,
  isCancelled,
}: TransactionProps) {
  const privyWallet = usePrivyWallet()
  const connectedWallet = useConnectedWallet()
  const isInternalWallet =
    privyWallet && connectedWallet === WALLET_NAME.OpenSeaWallet

  const userTransaction = useFragment(
    graphql`
      fragment Transaction_userTransaction on UserTransactionType {
        nonce
        transactionHash
        state
        createdDate
      }
    `,
    userTransactionDataKey,
  )

  const receipt = useFragment(
    graphql`
      fragment Transaction_receipt on TransactionReceiptType {
        status
        transactionHash
        ...ApproveCollection_receipt
        ...ApproveCurrency_receipt
        ...DealAccept_receipt
        ...BulkTransfer_receipt
        ...CancelAllOrders_receipt
        ...CancelDeal_receipt
        ...CancelOrder_receipt
        ...Transfer_receipt
        ...CreateDropTransaction_receipt
        ...UnknownTransaction_receipt
        ...Redeem_receipt
        ...ConfigureDrop_receipt
      }
    `,
    receiptDataKey,
  )

  const metadata = useFragment(
    graphql`
      fragment Transaction_metadata on TransactionMetadataType {
        type
        ...AcceptOffer_metadata
        ...OfferAccepted_metadata
        ...DealAccept_metadata
        ...ApproveCollection_metadata
        ...ApproveCurrency_metadata
        ...BulkTransfer_metadata
        ...CancelOrder_metadata
        ...CancelDeal_metadata
        ...PaymentAssetTransfer_metadata
        ...Purchase_metadata
        ...Sale_metadata
        ...Mint_metadata
        ...Transfer_metadata
        ...Unwrap_metadata
        ...Wrap_metadata
        ...Redeem_metadata
        ...ConfigureDrop_metadata
        ...Swap_metadata
        ...Receive_metadata
      }
    `,
    metadataDataKey,
  )

  let state: TxState
  if (userTransaction?.state) {
    state = userTransaction.state
  } else if (isCancelled) {
    state = "CANCELLED"
  } else if (receipt?.status) {
    state = "SUCCESSFUL"
  } else {
    state = "REJECTED"
  }

  const transactionHash =
    userTransaction?.transactionHash ?? receipt?.transactionHash

  // we only need nonce for cancellations, which are only available for internal wallets
  const nonce =
    isInternalWallet && userTransaction ? userTransaction.nonce : undefined

  const createdDate = userTransaction?.createdDate

  if (state === "REJECTED") {
    return (
      <UnknownTransaction
        createdDate={createdDate}
        nonce={nonce}
        receipt={receipt}
        state={state}
        transactionHash={transactionHash}
      />
    )
  }

  switch (metadata?.type) {
    case "APPROVE_COLLECTION":
      return (
        <ApproveCollection
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "ERC20_APPROVE":
      return (
        <ApproveCurrency
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "BULK_SENT":
    case "BULK_RECEIVED":
    case "ASSETS_SENT":
      return (
        <BulkTransfer
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "CANCEL_ALL_ORDERS":
      return (
        <CancelAllOrders
          createdDate={createdDate}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "CANCEL_ASK":
    case "CANCEL_BID":
    case "CANCEL_COLLECTION_OFFFER":
    case "CANCEL_ORDERS": // Todo: Support a different UI for cancelling multiple orders
      return (
        <CancelOrder
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "CANCEL_SWAP":
      return (
        <CancelDeal
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "BULK_PURCHASE":
    case "BUY_NOW":
      return (
        <Purchase
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "BULK_SALE":
    case "SALE":
      return (
        <Sale
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "BULK_ACCEPT_OFFER":
    case "ACCEPT_OFFER":
      return (
        <AcceptOffer
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "OFFER_ACCEPTED":
    case "BULK_OFFER_ACCEPTED":
      return (
        <OfferAccepted
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "ACCEPT_SWAP":
    case "SWAP_COMPLETE":
      return (
        <DealAccept
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "MINT":
    case "MINT_ALLOWLIST":
      return (
        <Mint
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "NATIVE_SENT":
    case "ERC20_SENT":
    case "NATIVE_RECEIVED":
    case "ERC20_RECEIVED":
      return (
        <PaymentAssetTransfer
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "NFT_SENT":
    case "NFT_RECEIVED":
      return (
        <Transfer
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "UNWRAP":
      return (
        <Unwrap
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "WRAP":
      return (
        <Wrap
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "CREATE_SEADROP_CONTRACT":
      return (
        <CreateDropTransaction
          createdDate={createdDate}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )
    case "CONFIGURE_DROP":
      return (
        <ConfigureDrop
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "OPENSEA_PRO_TRADE":
      return <OpenSeaProTrade state={state} transactionHash={transactionHash} />

    case "REDEEM":
      return (
        <Redeem
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "ERC20_SWAP_DEPOSIT":
    case "BRIDGE_DEPOSIT":
      return (
        <Swap
          createdDate={createdDate}
          metadata={metadata}
          nonce={nonce}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "ASSETS_RECEIVED":
      return (
        <Receive
          metadata={metadata}
          state={state}
          transactionHash={transactionHash}
        />
      )

    case "UNKNOWN":
    default:
      return (
        <UnknownTransaction
          createdDate={createdDate}
          nonce={nonce}
          receipt={receipt}
          state={state}
          transactionHash={transactionHash}
        />
      )
  }
}
